import React, { useState } from 'react';
import { X, Send, GraduationCap, Building2, User } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for creating a new license (apprentice or master). Extracted
 * from AdminPanel.jsx on 28/05/2026. Self-contained — owns its form
 * state, fires onCreated(licenseDoc) when the API returns 200.
 */
export default function CreateLicenseModal({ open, onClose, onCreated, token, masterLicenses }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [owner, setOwner] = useState('');
  const [maxIps, setMaxIps] = useState(0);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Pedagogical supervision fields.
  const [licenseType, setLicenseType] = useState('user'); // 'user' | 'master'
  const [masterId, setMasterId] = useState(''); // optional parent master license id (user type only)
  const [establishment, setEstablishment] = useState(''); // free text, only for master
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const isMaster = licenseType === 'master';

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name || !expiresAt) { setError("Nom et date d'expiration requis"); return; }
    setLoading(true);
    setError('');
    try {
      const body = {
        holder_name: name,
        holder_email: email,
        expires_at: new Date(expiresAt).toISOString(),
        notes, owner,
        max_ips: maxIps,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        is_master: isMaster,
        master_license_id: !isMaster && masterId ? masterId : null,
        establishment: isMaster ? (establishment || '') : '',
      };
      const res = await fetch(`${API}/api/licenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Erreur création');
      const data = await res.json();
      onCreated(data);
      setName(''); setEmail(''); setExpiresAt(''); setNotes(''); setOwner(''); setMaxIps(0); setContactName(''); setContactEmail(''); setContactPhone('');
      setLicenseType('user'); setMasterId(''); setEstablishment('');
      onClose();
    } catch { setError('Erreur lors de la création'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-license-modal">
        <div className="px-5 py-4 border-b border-ink-600 flex items-center justify-between sticky top-0 bg-ink-800 z-10">
          <h3 className="font-bold text-slate-100">Nouvelle licence</h3>
          <button onClick={onClose} className="p-1 hover:bg-ink-700/60 rounded"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleCreate} className="p-5 space-y-3">
          {/* Type toggle */}
          <div>
            <label className="text-xs font-medium text-slate-400">Type de licence</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                onClick={() => { setLicenseType('user'); setMasterId(''); }}
                className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                  licenseType === 'user'
                    ? 'bg-accent/15 border-accent text-accent'
                    : 'bg-background border-ink-600 text-slate-400 hover:text-slate-200'
                }`}
                data-testid="license-type-user"
              >
                <User className="w-3.5 h-3.5 inline mr-1" />
                Apprenant (joueur)
              </button>
              <button
                type="button"
                onClick={() => setLicenseType('master')}
                className={`px-3 py-2 rounded-md text-xs font-semibold border transition-colors ${
                  licenseType === 'master'
                    ? 'bg-purple-500/15 border-purple-400 text-purple-300'
                    : 'bg-background border-ink-600 text-slate-400 hover:text-slate-200'
                }`}
                data-testid="license-type-master"
              >
                <GraduationCap className="w-3.5 h-3.5 inline mr-1" />
                Responsable Pédagogique
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              {isMaster
                ? 'Licence de supervision : ne permet pas de jouer, mais d\'observer une cohorte d\'apprenants.'
                : 'Licence joueur classique. Peut être rattachée à une cohorte ci-dessous.'}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-400">Nom du titulaire *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="license-holder-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="license-holder-email" />
          </div>

          {/* Cohort selection (apprenant only) */}
          {!isMaster && (
            <div>
              <label className="text-xs font-medium text-slate-400">Rattacher à une cohorte (optionnel)</label>
              <select
                value={masterId}
                onChange={e => setMasterId(e.target.value)}
                className="w-full bg-background text-foreground border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent"
                data-testid="license-master-select"
              >
                <option value="">— Aucune (apprenant indépendant) —</option>
                {(masterLicenses || []).map(m => (
                  <option key={m.id} value={m.id}>
                    {m.holder_name} ({m.license_key})
                  </option>
                ))}
              </select>
              {masterLicenses && masterLicenses.length === 0 && (
                <p className="text-[10px] text-slate-500 mt-1">Aucun Responsable Pédagogique encore créé.</p>
              )}
            </div>
          )}

          {/* Establishment (master only) */}
          {isMaster && (
            <div>
              <label className="text-xs font-medium text-slate-400">Établissement (optionnel)</label>
              <input
                value={establishment}
                onChange={e => setEstablishment(e.target.value)}
                placeholder="Ex: Lycée Jean Moulin — Formation Transport"
                className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="license-establishment"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Texte libre. Tous les Responsables partageant le même libellé seront groupés dans l'onglet « Établissements ».
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-400">Propriétaire / Assignation (champ libre)</label>
            <input value={owner} onChange={e => setOwner(e.target.value)} placeholder="Ex: Entreprise XYZ — Service formation" className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="license-owner" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400">Date d'expiration *</label>
            <input type="date" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="license-expires" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400">Limite d'IP (0 = illimité)</label>
            <input type="number" min={0} value={maxIps} onChange={e => setMaxIps(parseInt(e.target.value) || 0)} className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="license-max-ips" />
            <p className="text-[10px] text-slate-500 mt-1">Nombre maximum d'adresses IP distinctes autorisées. 0 = pas de limite.</p>
          </div>
          <div className="bg-accent/10 rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-blue-800">Contact primaire (notifications d'expiration)</p>
            <div>
              <label className="text-xs font-medium text-slate-400">Nom du contact</label>
              <input value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Responsable IT" className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400">Email du contact *</label>
              <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="contact@entreprise.com" className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="contact-email" />
              <p className="text-[10px] text-accent mt-1">Recevra un email J-30 et une relance J-7 avant expiration.</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400">Téléphone (optionnel)</label>
              <input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+33 6 00 00 00 00" className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-accent" data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="w-full bg-background text-foreground placeholder-slate-500 border border-ink-600 rounded-md px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-2 focus:ring-accent" data-testid="license-notes" />
          </div>
          {error && <p className="text-xs text-danger">{error}</p>}
          <button type="submit" disabled={loading} className={`w-full py-2.5 rounded-lg font-bold text-sm disabled:opacity-50 transition-colors ${isMaster ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-accent text-white hover:bg-accent-hover'}`} data-testid="create-license-submit">
            {loading ? 'Création...' : isMaster ? 'Créer la licence Responsable' : 'Créer la licence Apprenant'}
          </button>
        </form>
      </div>
    </div>
  );
}
